/* ============================================================
   PROP & GROW — WISHLIST
   - Works for everyone: the list is kept in this browser
   - Signed-in customers: also saved to their account through
     ai.propandgrow.com/wishlist-api (signed by snippets/pg-wishlist-auth)
   - Buttons: any element with [data-pgwl-add] + data-handle/title/image/price/available
   - Counts: [data-pgwl-count] (badge) and [data-pgwl-count-text]
   ============================================================ */
(() => {
  if (window.PGWishlist) return;

  const CFG = window.PGWishlistConfig || {};
  const KEY = 'pg-wishlist-v1';
  const SYNC_KEY = 'pg-wishlist-synced-v1'; // handles the server knew about at the last sync
  const MAX = 200;
  const listeners = new Set();

  // ---------- storage ----------
  const read = () => {
    try {
      const d = JSON.parse(localStorage.getItem(KEY) || 'null');
      return d && Array.isArray(d.items) ? d : { items: [], updatedAt: 0 };
    } catch (e) { return { items: [], updatedAt: 0 }; }
  };
  let data = read();
  const write = () => {
    data.updatedAt = Date.now();
    try { localStorage.setItem(KEY, JSON.stringify(data)); } catch (e) { /* full / private mode */ }
  };

  const clean = it => ({
    handle: String(it.handle || '').toLowerCase(),
    title: String(it.title || ''),
    image: String(it.image || ''),
    price: Number(it.price) || 0,
    available: it.available === true || it.available === 'true',
    note: String(it.note || ''),
    done: !!it.done,
    addedAt: Number(it.addedAt) || Date.now()
  });

  // ---------- account sync ----------
  const syncOn = !!(CFG.auth && CFG.api);
  let syncState = syncOn ? 'syncing' : (CFG.signedIn ? 'device' : 'guest');
  let pushTimer = null;

  const syncedSet = () => { try { return new Set(JSON.parse(localStorage.getItem(SYNC_KEY) || '[]')); } catch (e) { return new Set(); } };
  const rememberSynced = items => { try { localStorage.setItem(SYNC_KEY, JSON.stringify(items.map(i => i.handle))); } catch (e) { /* ignore */ } };

  const push = async () => {
    if (!syncOn) return;
    try {
      const res = await fetch(`${CFG.api}/list`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...CFG.auth, items: data.items })
      });
      if (!res.ok) throw new Error(res.status);
      rememberSynced(data.items);
      syncState = 'synced';
    } catch (e) { syncState = 'offline'; }
    emit();
  };
  const pushSoon = () => { if (!syncOn) return; clearTimeout(pushTimer); pushTimer = setTimeout(push, 600); };

  const pull = async () => {
    if (!syncOn) return;
    try {
      const qs = new URLSearchParams(CFG.auth).toString();
      const res = await fetch(`${CFG.api}/list?${qs}`);
      if (!res.ok) throw new Error(res.status);
      const server = await res.json();
      const known = syncedSet();
      const serverItems = (server.items || []).map(clean);
      const serverHandles = new Set(serverItems.map(i => i.handle));
      // keep the account's order, then add anything saved on this device since the last sync
      // (items that were synced before but are gone from the account were removed elsewhere)
      const localNew = data.items.filter(i => !serverHandles.has(i.handle) && !known.has(i.handle));
      const merged = serverItems.slice();
      // newer local edits (notes / ticks) win for items both sides have
      const localByHandle = new Map(data.items.map(i => [i.handle, i]));
      if (data.updatedAt > (server.updatedAt || 0)) {
        merged.forEach((it, n) => { const l = localByHandle.get(it.handle); if (l) merged[n] = { ...it, note: l.note, done: l.done }; });
      }
      data.items = [...merged, ...localNew].slice(0, MAX);
      write();
      rememberSynced(serverItems);
      syncState = 'synced';
      emit();
      if (localNew.length || data.updatedAt > (server.updatedAt || 0)) push();
    } catch (e) { syncState = 'offline'; emit(); }
  };

  // ---------- API ----------
  const emit = () => {
    updateBadges();
    updateButtons();
    listeners.forEach(fn => { try { fn(data.items, syncState); } catch (e) { /* ignore */ } });
  };
  const change = () => { write(); emit(); pushSoon(); };

  const api = {
    items: () => data.items.slice(),
    has: h => data.items.some(i => i.handle === h),
    add(item) {
      const it = clean(item);
      if (!it.handle || api.has(it.handle)) return false;
      data.items.push(it);
      if (data.items.length > MAX) data.items.shift();
      change();
      return true;
    },
    remove(h) { data.items = data.items.filter(i => i.handle !== h); change(); },
    update(h, patch) { const it = data.items.find(i => i.handle === h); if (it) { Object.assign(it, patch); change(); } },
    move(from, to) {
      if (from === to || from < 0 || to < 0 || from >= data.items.length || to >= data.items.length) return;
      const [it] = data.items.splice(from, 1);
      data.items.splice(to, 0, it);
      change();
    },
    replaceAll(items) { data.items = items.map(clean); change(); },
    refreshDetails(map) {
      // map: handle -> { title, image, price, available } from the live catalog
      let changed = false;
      data.items.forEach(it => {
        const m = map.get(it.handle);
        if (!m) return;
        ['title', 'image', 'price', 'available'].forEach(k => { if (m[k] !== undefined && m[k] !== it[k]) { it[k] = m[k]; changed = true; } });
      });
      if (changed) { write(); emit(); }
    },
    sync: () => syncState,
    refreshButtons() { updateBadges(); updateButtons(); },
    config: CFG,
    subscribe(fn) { listeners.add(fn); fn(data.items, syncState); return () => listeners.delete(fn); }
  };
  window.PGWishlist = api;

  // ---------- badges ----------
  function updateBadges() {
    const n = data.items.length;
    document.querySelectorAll('[data-pgwl-count]').forEach(el => { el.textContent = n > 99 ? '99+' : String(n); el.hidden = n === 0; });
    document.querySelectorAll('[data-pgwl-count-text]').forEach(el => { el.textContent = n ? `(${n})` : ''; });
  }

  // ---------- add / remove buttons ----------
  function updateButtons() {
    document.querySelectorAll('[data-pgwl-add]').forEach(btn => {
      const on = api.has(btn.dataset.handle);
      btn.classList.toggle('is-on', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      const label = btn.querySelector('[data-pgwl-label]');
      if (label) label.textContent = on ? (btn.dataset.labelOn || 'On your wishlist') : (btn.dataset.labelOff || 'Add to wishlist');
      if (!label) btn.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Add to wishlist');
    });
  }

  // ---------- toast ----------
  let toastEl = null, toastTimer = null;
  const toast = html => {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'pgwl-toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML = html;
    toastEl.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-on'), 6500);
  };
  const esc = s => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-pgwl-add]');
    if (btn) {
      e.preventDefault();
      e.stopPropagation();
      const h = btn.dataset.handle;
      if (api.has(h)) {
        api.remove(h);
        toast(`<span>Removed from your wishlist.</span>`);
        return;
      }
      api.add({
        handle: h,
        title: btn.dataset.title,
        image: btn.dataset.image,
        price: btn.dataset.price,
        available: btn.dataset.available
      });
      btn.classList.remove('pop'); void btn.offsetWidth; btn.classList.add('pop');
      const page = CFG.page || '/collections/all?view=wishlist';
      if (syncOn) {
        toast(`<span><strong>Added to your list ♥</strong> Saved to your account.</span><a href="${esc(page)}">View list</a>`);
      } else if (!CFG.signedIn) {
        toast(`<span><strong>Added to your list ♥</strong> It's saved on this device. Sign up free to keep it everywhere.</span><a href="${esc(CFG.loginUrl || '/account/login')}">Sign up</a><a href="${esc(page)}">View list</a>`);
      } else {
        toast(`<span><strong>Added to your list ♥</strong></span><a href="${esc(page)}">View list</a>`);
      }
      return;
    }
    if (e.target.closest('.pgwl-toast a')) toastEl && toastEl.classList.remove('is-on');
  });

  // keep tabs in step
  window.addEventListener('storage', e => { if (e.key === KEY) { data = read(); emit(); } });

  const start = () => { emit(); pull(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
